import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { translateFarmsError } from '../lib/translateError';

const BUCKET = 'farm-photos';

export interface FarmPhoto {
  id:           string;
  storage_path: string;
  url:          string;
}

export function useFarmPhotos(farmId: string) {
  const [photos, setPhotos]       = useState<FarmPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const loadPhotos = useCallback(async () => {
    if (!farmId) return;
    const { data } = await supabase
      .from('farm_photos')
      .select('id, storage_path, display_order')
      .eq('farm_id', farmId)
      .order('display_order');
    if (data) {
      const rows = data as { id: string; storage_path: string; display_order: number }[];
      setPhotos(
        rows.map((p) => ({
          id:           p.id,
          storage_path: p.storage_path,
          url:          supabase.storage.from(BUCKET).getPublicUrl(p.storage_path).data?.publicUrl ?? '',
        }))
      );
    }
  }, [farmId]);

  useEffect(() => { void loadPhotos(); }, [loadPhotos]);

  async function uploadPhoto(file: File): Promise<void> {
    if (!farmId) return;
    setUploading(true);
    setError(null);
    try {
      const ext  = file.name.split('.').pop() ?? 'jpg';
      const path = `${farmId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file);
      if (upErr) throw new Error(translateFarmsError(upErr.message));
      const { error: dbErr } = await supabase.from('farm_photos').insert({
        farm_id:       farmId,
        storage_path:  path,
        display_order: photos.length,
      } as never);
      if (dbErr) throw new Error(translateFarmsError(dbErr.message));
      await loadPhotos();
    } catch (e: any) {
      setError(e.message ?? 'Erro ao enviar foto.');
    } finally {
      setUploading(false);
    }
  }

  async function deletePhoto(id: string, storagePath: string): Promise<void> {
    setError(null);
    try {
      const { error: storErr } = await supabase.storage.from(BUCKET).remove([storagePath]);
      if (storErr) throw new Error(translateFarmsError(storErr.message));
      const { error: dbErr } = await supabase.from('farm_photos').delete().eq('id', id);
      if (dbErr) throw new Error(translateFarmsError(dbErr.message));
      setPhotos((p) => p.filter((ph) => ph.id !== id));
    } catch (e: any) {
      setError(e.message ?? 'Erro ao excluir foto.');
    }
  }

  return { photos, uploading, error, uploadPhoto, deletePhoto, reload: loadPhotos };
}

export async function uploadFarmLogo(farmId: string, file: File): Promise<string | null> {
  const ext  = file.name.split('.').pop() ?? 'jpg';
  const path = `logos/${farmId}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
  if (error) return null;
  return supabase.storage.from(BUCKET).getPublicUrl(path).data?.publicUrl ?? null;
}
